/**
 * dbt failure causes (S7, contract §3): a closed reason code plus identifiers that passed a fixed
 * rule, classified INSIDE the dbt container (`transform/skydata_dbt/causes.py`). dbt's and the
 * warehouse's own text never leaves the container, because Snowflake quotes row values in the
 * same `'...'` syntax it uses for identifiers (SEC-17).
 *
 * The code list mirrors `transform/reason-codes.json` (generated from `causes.CODES`), and every
 * field rule here has a Python twin in `causes.FIELD_RULES`; `dbtCauses.test.ts` and
 * `transform/tests/test_causes.py` run ONE fixture (`transform/tests/fixtures/causes/
 * validator-cases.json`) through both, so the two cannot drift.
 *
 * Everything that reads a cause from a blob, a queue message, a `run_events` payload or
 * `run_steps.metrics` goes through {@link parseDbtCause}: it field-selects the keys of
 * {@link DbtCause}, re-validates each one and omits whatever fails. {@link describeDbtCause}
 * interpolates only what that returns, so a sentence can carry identifiers and counts, nothing
 * else.
 */

import {
  WAREHOUSE_ERROR_HINTS,
  warehouseErrorKind,
  type WarehouseErrorKind,
} from "../warehouse/warehouseErrorKinds";

export const DBT_CAUSE_CODES = [
  "COLUMN_NOT_FOUND",
  "RELATION_NOT_FOUND",
  "SQL_SYNTAX_ERROR",
  "VALUE_CONVERSION_FAILED",
  "STATEMENT_TIMEOUT",
  "WAREHOUSE_UNREACHABLE",
  "NO_CURRENT_DATABASE",
  "WAREHOUSE_ERROR",
  "MODEL_COMPILATION_ERROR",
  "DBT_RUNTIME_ERROR",
  "UPSTREAM_FAILED",
  "UNCLASSIFIED",
  "TEST_UNIQUE_FAILED",
  "TEST_NOT_NULL_FAILED",
  "TEST_RELATIONSHIPS_FAILED",
  "TEST_ACCEPTED_VALUES_FAILED",
  "TEST_SINGULAR_FAILED",
  "TEST_GENERIC_FAILED",
  "REF_NOT_FOUND",
  "SOURCE_NOT_FOUND",
  "PROJECT_PARSE_ERROR",
  "DUPLICATE_RESOURCE",
  "SELECTOR_NOT_VALID",
  // #1442: a selected model would build over a table a connection loads; refused before dbt run.
  "MODEL_TARGETS_LANDED_TABLE",
  "PHASE_CONFIG",
  "PHASE_AUTH",
  "PHASE_CLONE",
  "PHASE_DEPS",
  "PHASE_COMPILE",
  "PHASE_RUN",
  "PHASE_TEST",
  "PHASE_INFRA",
  "PHASE_TIMEOUT",
  "PHASE_INTERNAL",
  // Round 7: a source upstream of the selection was past its freshness limit (exit 20).
  "PHASE_STALE",
] as const;
export type DbtCauseCode = (typeof DBT_CAUSE_CODES)[number];

/**
 * #1674: why a dbt checkout failed, a closed field on `PHASE_CLONE` (`cloneReason`). The image reads
 * git's standard error once, in memory (`causes.clone_reason`), and keeps only this word; git's own
 * text never leaves the container. The list mirrors `cloneReasons` in `transform/reason-codes.json`.
 */
export const DBT_CLONE_REASONS = ["ref_not_found", "repo_unreadable", "auth", "unknown"] as const;
export type DbtCloneReason = (typeof DBT_CLONE_REASONS)[number];

/** The cause's clone reason, or null: only a `PHASE_CLONE` carries one. */
export function dbtCloneReasonOf(
  cause: { code: string; cloneReason?: unknown } | null | undefined,
): DbtCloneReason | null {
  if (cause?.code !== "PHASE_CLONE") return null;
  const reason = cause.cloneReason;
  return typeof reason === "string" && (DBT_CLONE_REASONS as readonly string[]).includes(reason)
    ? (reason as DbtCloneReason)
    : null;
}

const OBJECT_KINDS = [
  "table",
  "view",
  "schema",
  "database",
  "stage",
  "function",
  "object",
] as const;
const TARGET_TYPES = ["NUMBER", "DATE"] as const;
export const DBT_TEST_KINDS = [
  "unique",
  "not_null",
  "relationships",
  "accepted_values",
  "singular",
  "generic",
  // Session 11: a dbt unit test (`resource_type: unit_test`, `dbt-manifest-summary/1.3.0`).
  "unit",
] as const;
const SEVERITIES = ["error", "warn"] as const;
const DBT_ERROR_TYPES = ["Database", "Compilation", "Runtime", "Internal"] as const;

/** A classified cause. Every key but `code` is optional and present only when it passed its rule. */
export type DbtCause = {
  code: DbtCauseCode;
  node?: string;
  model?: string;
  upstream?: string;
  nodes?: string[];
  column?: string;
  relation?: string;
  objectKind?: (typeof OBJECT_KINDS)[number];
  targetType?: (typeof TARGET_TYPES)[number];
  line?: number;
  position?: number;
  warehouseCode?: string;
  sqlState?: string;
  target?: string;
  disabled?: boolean;
  path?: string;
  symbol?: string;
  testKind?: (typeof DBT_TEST_KINDS)[number];
  testName?: string;
  parent?: string;
  parentColumn?: string;
  failures?: number;
  /** #1442: how many selected models would overwrite a loaded table (`MODEL_TARGETS_LANDED_TABLE`). */
  collisions?: number;
  severity?: (typeof SEVERITIES)[number];
  exitClass?: string;
  dbtErrorType?: (typeof DBT_ERROR_TYPES)[number];
  /** #1674: on `PHASE_CLONE` only, why the checkout failed (a closed word, never git's text). */
  cloneReason?: DbtCloneReason;
};
export type DbtTestKind = NonNullable<DbtCause["testKind"]>;

/**
 * #1292: the step summary of the runner's own selector refusal, before dbt starts. A closed code
 * and nothing else, so no record content can ride on it (SEC-17 S7 reads it as `summary.cause`).
 */
export const SELECTOR_REFUSAL_SUMMARY: { readonly cause: DbtCause } = Object.freeze({
  cause: Object.freeze({ code: "SELECTOR_NOT_VALID" as const }),
});

/** What the API returns for a cause: the server's sentence and label beside the identifiers. */
export type DbtCauseView = {
  code: DbtCauseCode;
  label: string;
  sentence: string;
  identifiers: Omit<DbtCause, "code">;
};

// ---------------------------------------------------------------------------------------------
// Closed sets for fields that are not cause keys
// ---------------------------------------------------------------------------------------------

export const DBT_RESULT_STATUSES = [
  "success",
  "error",
  "fail",
  "warn",
  "skipped",
  "pass",
  "runtime error",
  "no-op",
  "partial success",
] as const;
export type DbtResultStatus = (typeof DBT_RESULT_STATUSES)[number];

/**
 * The dbt result statuses that count as FAILED, everywhere: the runner's `failed` flag, its
 * `modelsFailed` count and the `DBT_MODEL` event level, the graph's node `failed`, and the
 * history's `failed` outcome and failed-test count all read this one set. `partial success` is
 * in it: dbt reports it for a microbatch model with at least one failed batch, which is a
 * failure to investigate, not a success.
 */
export const DBT_FAILED_RESULT_STATUSES = [
  "error",
  "fail",
  "runtime error",
  "partial success",
] as const satisfies readonly DbtResultStatus[];

const FAILED_RESULT_STATUS_SET: ReadonlySet<string> = new Set(DBT_FAILED_RESULT_STATUSES);

/** Whether a (constrained) dbt status counts as failed; see {@link DBT_FAILED_RESULT_STATUSES}. */
export function isDbtFailedStatus(status: string): boolean {
  return FAILED_RESULT_STATUS_SET.has(status);
}

/** dbt's node status, trimmed and lower-cased, in the closed set; anything else `unknown`. */
export function toDbtResultStatus(value: unknown): DbtResultStatus | "unknown" {
  if (typeof value !== "string") return "unknown";
  const lowered = value.trim().toLowerCase();
  return (DBT_RESULT_STATUSES as readonly string[]).includes(lowered)
    ? (lowered as DbtResultStatus)
    : "unknown";
}

export const DBT_MATERIALIZATIONS = [
  "table",
  "view",
  "incremental",
  "ephemeral",
  "materialized_view",
  "dynamic_table",
  "snapshot",
  "seed",
  "test",
  "unit",
] as const;
export type DbtMaterialization = (typeof DBT_MATERIALIZATIONS)[number];

/** A materialization in the closed set, `other` for any other word, null for nothing. */
export function toDbtMaterialization(value: unknown): DbtMaterialization | "other" | null {
  if (typeof value !== "string" || value.trim() === "") return null;
  const lowered = value.trim().toLowerCase();
  return (DBT_MATERIALIZATIONS as readonly string[]).includes(lowered)
    ? (lowered as DbtMaterialization)
    : "other";
}

/**
 * #1093: the materializations that report no row count, each with the words a Rows cell shows. A
 * view stores no rows and an ephemeral model is inlined into its callers. A materialized view and
 * a dynamic table are built by one CREATE statement and filled by Snowflake, so the 1 dbt's
 * Snowflake adapter reports for any of the four is that statement, not a row
 * (`transform/skydata_dbt/rowcounts.py` substitutes real counts for `table` only).
 */
const NO_ROWS_LABELS: ReadonlyMap<string, string> = new Map([
  ["view", "A view stores no rows"],
  ["ephemeral", "An ephemeral model stores no rows"],
  ["materialized_view", "A materialized view reports no row count"],
  ["dynamic_table", "A dynamic table reports no row count"],
]);

/** #1093: why a node of this materialization has no row count, or null when it reports one. */
export function dbtNoRowsLabel(materialization: unknown): string | null {
  const kind = toDbtMaterialization(materialization);
  return kind === null ? null : (NO_ROWS_LABELS.get(kind) ?? null);
}

/**
 * #1093: the one rule for a dbt node's row count, applied where a node's outcome is read (so an
 * older stored run reads the same): null for a materialization in `NO_ROWS_LABELS`, else
 * `rowsAffected`.
 * An unknown materialization keeps the count.
 */
export function dbtNodeRows(materialization: unknown, rowsAffected: number | null): number | null {
  if (rowsAffected === null) return null;
  return dbtNoRowsLabel(materialization) === null ? rowsAffected : null;
}

/**
 * #1443: the result statuses that wrote rows in their own run. `success`, and `partial success`:
 * dbt reports it for a microbatch (incremental) model whose committed batches did write in this
 * run, and its count is dbt's own answer for them (the image re-reads only a `success` table's
 * count from the catalogue), so it is kept though the result reads Failed.
 */
const ROWS_WRITTEN_STATUSES: ReadonlySet<string> = new Set(["success", "partial success"]);

/** #1443: whether a result of this status wrote rows in its own run ({@link dbtResultRows}). */
export function dbtResultWroteRows(status: unknown): boolean {
  return ROWS_WRITTEN_STATUSES.has(toDbtResultStatus(status));
}

/**
 * #1443: the rows one result of a run wrote. A failed, errored or skipped `table` model's count
 * is the table an EARLIER build left (the image once re-read it from the catalogue), so it reads
 * as no count, a dash, beside its Failed badge; only `success` and `partial success` keep one
 * ({@link dbtResultWroteRows}). Applied wherever a stored `DBT_MODEL` payload is read (the run's
 * dbt results, the job history, the Models tab's last build and the Events sentence), so a run
 * stored before the image fix reads the same. Then the materialization rule of
 * {@link dbtNodeRows}.
 */
export function dbtResultRows(
  status: unknown,
  materialization: unknown,
  rowsAffected: number | null,
): number | null {
  if (!dbtResultWroteRows(status)) return null;
  return dbtNodeRows(materialization, rowsAffected);
}

export const DBT_RESOURCE_TYPES = [
  "model",
  "seed",
  "snapshot",
  "source",
  "test",
  "unit_test",
  "analysis",
  "operation",
  "exposure",
  "metric",
  "semantic_model",
  "saved_query",
] as const;
export type DbtResourceType = (typeof DBT_RESOURCE_TYPES)[number];

export function toDbtResourceType(value: unknown): DbtResourceType | "other" {
  if (typeof value !== "string") return "other";
  const lowered = value.trim().toLowerCase();
  return (DBT_RESOURCE_TYPES as readonly string[]).includes(lowered)
    ? (lowered as DbtResourceType)
    : "other";
}

/** The `class` values of `transform/exit-codes.json`; `dbtCauses.test.ts` asserts the two agree. */
export const DBT_CAUSE_EXIT_CLASSES: readonly string[] = [
  "ok",
  "config",
  "auth",
  "clone",
  "deps",
  "compile",
  "run",
  "test",
  "infra",
  "timeout",
  "internal",
  "stale",
];

// ---------------------------------------------------------------------------------------------
// Field rules (identical regexes in `causes.py`)
// ---------------------------------------------------------------------------------------------

/**
 * Round 8: a segment may hold a hyphen, never at its start (dbt allows `seeds/lookup-codes.csv`
 * and `models/stg-orders.sql`, whose ids are `seed.p.lookup-codes` and `model.p.stg-orders`).
 */
const UNIQUE_ID_RE =
  /^(model|test|seed|snapshot|source|unit_test)\.[A-Za-z0-9_][A-Za-z0-9_-]*(\.[A-Za-z0-9_][A-Za-z0-9_-]*){1,3}$/;
const IDENTIFIER_RE = /^"?[A-Za-z_][A-Za-z0-9_$]*"?(\."?[A-Za-z_][A-Za-z0-9_$]*"?){0,3}$/;
const QUOTED_SEGMENT_RE = /"([^"]*)"/g;
const SYMBOL_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
/**
 * Round 8 review: a dbt resource name may hold a hyphen, never at its start
 * (`tests/assert-positive.sql`, `ref('stg-orders')`); `causes.py` takes the same rule.
 */
const TEST_NAME_RE = /^[A-Za-z0-9_][A-Za-z0-9_-]*(\.[A-Za-z0-9_][A-Za-z0-9_-]*)?$/;
/** A dbt name as a ref target or a relationships parent (`stg-orders`, `raw-src.dbo-orders`). */
const DBT_NAME_RE = /^[A-Za-z0-9_][A-Za-z0-9_-]*(\.[A-Za-z0-9_][A-Za-z0-9_-]*){0,3}$/;
const PATH_RE = /^[A-Za-z0-9_.\-/]{1,512}$/;
const WAREHOUSE_CODE_RE = /^[0-9]{6}$/;
const SQL_STATE_RE = /^[0-9A-Z]{5}$/;
/**
 * Session 11 (ruling §8.8): a segment may also be backtick-quoted (`` `SCH` ``), the way
 * dbt-databricks renders a relation, so a Databricks relation keeps its schema once FR-63 lands.
 * `RELATION_NAME_RE` in `transform/skydata_dbt/causes.py` takes the same alternative.
 */
const RELATION_PART = '(?:[A-Za-z_][A-Za-z0-9_$]*|"[A-Za-z0-9_$]+"|`[A-Za-z0-9_$]+`)';
/** One part, then up to three more after a dot. */
const RELATION_NAME_RE = new RegExp(String.raw`^${RELATION_PART}(?:\.${RELATION_PART}){0,3}$`);

function bounded(value: unknown, limit: number): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= limit;
}

/** The dbt unique_id rule (512 characters at most). */
export function isDbtUniqueId(value: unknown): value is string {
  return bounded(value, 512) && UNIQUE_ID_RE.test(value);
}

function isIdentifier(value: unknown): boolean {
  return bounded(value, 255) && IDENTIFIER_RE.test(value);
}

/** A ref target or relationships parent: the identifier rule, or a hyphen-aware dbt name. */
function isDbtName(value: unknown): boolean {
  return isIdentifier(value) || (bounded(value, 255) && DBT_NAME_RE.test(value));
}

/** The identifier rule minus a string literal's shape (lowercase inside double quotes). */
function isColumn(value: unknown): boolean {
  if (!isIdentifier(value)) return false;
  for (const match of (value as string).matchAll(QUOTED_SEGMENT_RE)) {
    if (/[a-z]/.test(match[1] ?? "")) return false;
  }
  return true;
}

/** The dbt path rule: project-relative, path characters only, no `..` segment (512 at most). */
export function isDbtPath(value: unknown): value is string {
  if (!bounded(value, 512) || !PATH_RE.test(value)) return false;
  return !value.startsWith("/") && !value.split("/").includes("..");
}

const isPath = isDbtPath;

/**
 * A count from a dbt artifact: a non-negative safe integer, else nothing. The ONE rule for
 * `failures` and every other count, shared by the runner's `parseDbtTestRows` and the server's
 * `parseDbtTestResults`, which read the same blob (integration, review s7-O3 r3 nit 1).
 */
export function isDbtCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function oneOf(values: readonly string[], lower = false): (value: unknown) => boolean {
  return (value) =>
    typeof value === "string" && values.includes(lower ? value.toLowerCase() : value);
}

/** A warehouse relation name, or null. A quoted segment MAY hold lowercase letters here. */
export function toDbtRelationName(value: unknown): string | null {
  return bounded(value, 512) && RELATION_NAME_RE.test(value) ? value : null;
}

/** One predicate per field; a value that fails is omitted (the code is kept). */
export const DBT_CAUSE_FIELD_RULES: { [field: string]: (value: unknown) => boolean } = {
  code: (value) =>
    typeof value === "string" && (DBT_CAUSE_CODES as readonly string[]).includes(value),
  node: isDbtUniqueId,
  model: isDbtUniqueId,
  upstream: isDbtUniqueId,
  uniqueId: isDbtUniqueId,
  attachedNode: isDbtUniqueId,
  column: isColumn,
  relation: isIdentifier,
  parent: isDbtName,
  parentColumn: isIdentifier,
  target: isDbtName,
  symbol: (value) => bounded(value, 128) && SYMBOL_RE.test(value),
  namespace: (value) => bounded(value, 128) && SYMBOL_RE.test(value),
  testName: (value) => bounded(value, 255) && TEST_NAME_RE.test(value),
  path: isPath,
  exitClass: oneOf(DBT_CAUSE_EXIT_CLASSES),
  objectKind: oneOf(OBJECT_KINDS, true),
  targetType: oneOf(TARGET_TYPES),
  testKind: oneOf(DBT_TEST_KINDS),
  kind: oneOf(DBT_TEST_KINDS),
  severity: oneOf(SEVERITIES, true),
  dbtErrorType: oneOf(DBT_ERROR_TYPES),
  warehouseCode: (value) => typeof value === "string" && WAREHOUSE_CODE_RE.test(value),
  sqlState: (value) => typeof value === "string" && SQL_STATE_RE.test(value),
  line: isDbtCount,
  position: isDbtCount,
  failures: isDbtCount,
  collisions: isDbtCount,
  disabled: (value) => typeof value === "boolean",
  relationName: (value) => toDbtRelationName(value) !== null,
  cloneReason: oneOf(DBT_CLONE_REASONS),
};

/** Stored lower-cased once accepted (dbt writes `ERROR` and `warn`). */
const LOWERED = new Set(["objectKind", "severity"]);

/** The keys of {@link DbtCause}; nothing else on an input is ever read. */
const CAUSE_KEYS = [
  "node",
  "model",
  "upstream",
  "column",
  "relation",
  "objectKind",
  "targetType",
  "line",
  "position",
  "warehouseCode",
  "sqlState",
  "target",
  "disabled",
  "path",
  "symbol",
  "testKind",
  "testName",
  "parent",
  "parentColumn",
  "failures",
  "collisions",
  "severity",
  "exitClass",
  "dbtErrorType",
  "cloneReason",
] as const satisfies readonly (keyof DbtCause)[];

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** One cause field through its rule (lower-cased where the image lower-cases it); else undefined. */
function causeField(key: (typeof CAUSE_KEYS)[number], item: unknown): unknown {
  const rule = DBT_CAUSE_FIELD_RULES[key];
  if (item === null || item === undefined || !rule?.(item)) return undefined;
  return LOWERED.has(key) ? (item as string).toLowerCase() : item;
}

/** The dbt unique ids of a list, each once, in order. */
function distinctUniqueIds(entries: readonly unknown[]): string[] {
  const kept: string[] = [];
  for (const entry of entries) {
    if (isDbtUniqueId(entry) && !kept.includes(entry)) kept.push(entry);
  }
  return kept;
}

/**
 * Field-selects and re-validates a cause with the same rules as the image. Null unless `value`
 * is a plain object whose `code` is a known code; a key that fails its rule is omitted; an
 * unknown key is never copied. Never throws.
 */
export function parseDbtCause(value: unknown): DbtCause | null {
  try {
    if (!isPlainObject(value) || !DBT_CAUSE_FIELD_RULES.code!(value.code)) return null;
    const out: Record<string, unknown> = { code: value.code };
    for (const key of CAUSE_KEYS) {
      if (!(key in value)) continue;
      const item = causeField(key, value[key]);
      if (item !== undefined) out[key] = item;
    }
    if (Array.isArray(value.nodes)) {
      const kept = distinctUniqueIds(value.nodes);
      if (kept.length > 0) out.nodes = kept.slice(0, 2);
    }
    return out as DbtCause;
  } catch {
    return null;
  }
}

/** `causeCounts` re-validation: known codes, non-negative safe integers; empty → null. */
export function parseDbtCauseCounts(value: unknown): Partial<Record<DbtCauseCode, number>> | null {
  if (!isPlainObject(value)) return null;
  const out: Partial<Record<DbtCauseCode, number>> = {};
  let kept = 0;
  for (const code of DBT_CAUSE_CODES) {
    const count = value[code];
    if (isDbtCount(count)) {
      out[code] = count;
      kept += 1;
    }
  }
  return kept > 0 ? out : null;
}

/** A test node's `test` block of `dbt-manifest-summary/1.2.0`, re-validated; null without a known kind. */
export type DbtManifestTest = {
  kind: DbtTestKind;
  namespace?: string;
  column?: string;
  attachedNode?: string;
  severity?: (typeof SEVERITIES)[number];
};

export function parseDbtManifestTest(value: unknown): DbtManifestTest | null {
  if (!isPlainObject(value) || !DBT_CAUSE_FIELD_RULES.kind!(value.kind)) return null;
  const out: DbtManifestTest = { kind: value.kind as DbtTestKind };
  if (DBT_CAUSE_FIELD_RULES.namespace!(value.namespace)) out.namespace = value.namespace as string;
  if (DBT_CAUSE_FIELD_RULES.column!(value.column)) out.column = value.column as string;
  if (DBT_CAUSE_FIELD_RULES.attachedNode!(value.attachedNode)) {
    out.attachedNode = value.attachedNode as string;
  }
  if (DBT_CAUSE_FIELD_RULES.severity!(value.severity)) {
    out.severity = (value.severity as string).toLowerCase() as DbtManifestTest["severity"];
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Sentences and labels (contract §3.4)
// ---------------------------------------------------------------------------------------------

/** The display name of a unique id: the last segment, or for a generic test the one before its hash. */
export function dbtNodeName(uniqueId: string): string {
  const segments = uniqueId.split(".");
  if (segments[0] === "test" && segments.length >= 4) {
    return segments[segments.length - 2] || uniqueId;
  }
  return segments[segments.length - 1] || uniqueId;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** The verb form that agrees with a count: `one` for exactly one, else `many`. */
function agree(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

const PHASE_WORDS: Record<string, string> = {
  PHASE_CONFIG: "config",
  PHASE_AUTH: "auth",
  PHASE_CLONE: "clone",
  PHASE_DEPS: "deps",
  PHASE_COMPILE: "compile",
  PHASE_RUN: "run",
  PHASE_TEST: "test",
  PHASE_INFRA: "infra",
  PHASE_TIMEOUT: "timeout",
  PHASE_INTERNAL: "internal",
  PHASE_STALE: "stale",
};

/** "table" → "Table": the start of a sentence that has no node to lead with. */
function capitalised(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * The "{model} …" sentences when the cause names no node (§3.9: an error raised at invocation,
 * before any node reported). "A model: column X …" reads badly, and "A model failed with a dbt
 * runtime error" is wrong when a bad profile stopped the run before any model ran, so these lead
 * with the finding itself; the node forms are the contract's (§3.4) and are unchanged.
 */
function sentenceWithoutNode(cause: DbtCause): string | null {
  switch (cause.code) {
    case "COLUMN_NOT_FOUND":
      return cause.column
        ? `Column ${cause.column} was not found in what a dbt statement selects from. Was it renamed or removed at the source?`
        : "A column a dbt statement selects was not found. Was it renamed or removed at the source?";
    case "RELATION_NOT_FOUND":
      return cause.relation
        ? `${capitalised(cause.objectKind ?? "object")} ${cause.relation} was not found, or the dbt role cannot see it.`
        : `A ${cause.objectKind ?? "relation"} dbt reads was not found, or the dbt role cannot see it.`;
    case "SQL_SYNTAX_ERROR":
      return cause.line !== undefined && cause.position !== undefined
        ? `A compiled SQL statement has a syntax error at line ${cause.line}, position ${cause.position}.`
        : "A compiled SQL statement has a syntax error.";
    case "VALUE_CONVERSION_FAILED":
      return `A source value could not be converted to ${cause.targetType ?? "the target type"}. Check the column's data type at the source.`;
    case "STATEMENT_TIMEOUT":
      return "A dbt query reached the statement or warehouse timeout.";
    case "MODEL_COMPILATION_ERROR":
      return compilationSentence(cause, null);
    case "DBT_RUNTIME_ERROR":
      return "The dbt run stopped with a dbt runtime error.";
    case "MODEL_TARGETS_LANDED_TABLE":
      return landedTableSentence(cause, null);
    default:
      return null;
  }
}

/** "a" or "a and b": the names of the (at most two) unique ids a cause keeps. */
function nodeNames(nodes: readonly string[]): string {
  const names = nodes.map(dbtNodeName);
  return names.length >= 2 ? `${names[0]} and ${names[1]}` : (names[0] ?? "");
}

/**
 * The model a project file defines: a `.sql` file's name without its folder and extension
 * (`models/a/b.sql` is `b`). Null for any other file: a `.yml` properties file defines tests,
 * sources or snapshots under names of their own, so its stem names nothing (#1435 review).
 */
function sqlFileModel(path: string): string | null {
  const file = path.split("/").pop() ?? path;
  if (!file.toLowerCase().endsWith(".sql")) return null;
  const stem = file.slice(0, -".sql".length);
  return stem === "" ? null : stem;
}

/**
 * #1435: `MODEL_COMPILATION_ERROR` names what dbt names. A cause with `nodes` is dbt's ref cycle
 * (`Found a cycle: a --> b`, the image keeps at most two of its models, so the sentence says
 * "includes"); any other names the node, else the model of dbt's header file when it is a `.sql`
 * file (a model's name is its file's name), with the file beside it. With neither it speaks of
 * the project, the file still beside it (a `.yml` file names no one resource).
 */
function compilationSentence(cause: DbtCause, subject: string | null): string {
  if (cause.nodes && cause.nodes.length > 0) {
    return `The dbt project has a ref cycle that includes ${nodeNames(cause.nodes)}. Nothing was built in this run.`;
  }
  const name = subject ?? (cause.path ? sqlFileModel(cause.path) : null);
  const undefinedSymbol = cause.symbol ? `: ${cause.symbol} is undefined` : "";
  const where = cause.path ? ` (${cause.path})` : "";
  if (name === null) return `The dbt project could not be compiled${where}${undefinedSymbol}.`;
  return `${name} could not be compiled${where}${undefinedSymbol}.`;
}

/** The plural of the resource kind a unique id names (`model.p.a` is "models"). */
function resourceKindPlural(uniqueId: string): string {
  switch (uniqueId.split(".")[0]) {
    case "model":
      return "models";
    case "seed":
      return "seeds";
    case "snapshot":
      return "snapshots";
    case "source":
      return "sources";
    case "test":
      return "tests";
    case "unit_test":
      return "unit tests";
    default:
      return "resources";
  }
}

/**
 * #1435: `DUPLICATE_RESOURCE`. Two files of one name in different folders share ONE unique id,
 * so the image keeps that one id and the sentence names the shared name.
 */
function duplicateSentence(cause: DbtCause): string {
  const [first, second] = cause.nodes ?? [];
  if (first && second) {
    return `The dbt project defines ${first} and ${second} with the same name. Nothing was built in this run.`;
  }
  if (first) {
    return `The dbt project defines two ${resourceKindPlural(first)} named ${dbtNodeName(first)}. Nothing was built in this run.`;
  }
  return "The dbt project defines two resources with the same name. Nothing was built in this run.";
}

/**
 * #1442: the sentence of `MODEL_TARGETS_LANDED_TABLE`, with the model when the cause names one.
 * With more than one colliding model (`collisions`) it says how many, so a person does not read
 * one rename as the whole fix and meet the next refusal on the next run.
 */
function landedTableSentence(cause: DbtCause, model: string | null): string {
  const others = (cause.collisions ?? 1) - 1;
  const table = cause.relation ? relationTable(cause.relation) : null;
  if (others <= 0) {
    const subject = model ? `The dbt model ${model}` : "A dbt model";
    const target = table ? `the loaded table ${table}` : "a loaded table";
    return `${subject} would overwrite ${target}. Rename the model or set its schema.`;
  }
  const subject = model
    ? `The dbt model ${model} and ${plural(others, "other model", "other models")}`
    : `${others + 1} dbt models`;
  const first = table ? `, starting with ${table}` : "";
  return `${subject} would overwrite loaded tables${first}. Rename them or set their schema.`;
}

/**
 * #1442: the table a relation names, as a person reads it: its last segment, without the quotes
 * Snowflake needs for a name that is not upper case.
 */
function relationTable(relation: string): string {
  const last = /(?:^|\.)("(?:[^"]|"")*"|[^.]+)$/.exec(relation)?.[1] ?? relation;
  return last.startsWith('"') && last.endsWith('"') && last.length >= 2
    ? last.slice(1, -1).replaceAll('""', '"')
    : last;
}

/** What a node-form sentence reads from a cause, with its defaults. */
interface SentenceContext {
  cause: DbtCause;
  /** The node's display name, or null when the cause names none. */
  subject: string | null;
  model: string;
  testModel: string;
  column: string;
  failures: number | undefined;
}

/** "N rows" with the count, or the bare plural when the cause has no count. */
function foundRows(failures: number | undefined): string {
  return failures === undefined ? "rows" : plural(failures, "row", "rows");
}

/** The contract's node-form sentences (§3.4), one per code; a code not listed names a phase. */
const SENTENCES = new Map<DbtCauseCode, (context: SentenceContext) => string>([
  [
    "COLUMN_NOT_FOUND",
    ({ cause, model }) =>
      cause.column
        ? `${model}: column ${cause.column} was not found in what it selects from. Was it renamed or removed at the source?`
        : `${model}: a column it selects was not found. Was it renamed or removed at the source?`,
  ],
  [
    "RELATION_NOT_FOUND",
    ({ cause, model }) =>
      cause.relation
        ? `${model}: ${cause.objectKind ?? "object"} ${cause.relation} was not found, or the dbt role cannot see it.`
        : `${model}: a ${cause.objectKind ?? "relation"} it reads was not found, or the dbt role cannot see it.`,
  ],
  [
    "SQL_SYNTAX_ERROR",
    ({ cause, model }) =>
      cause.line !== undefined && cause.position !== undefined
        ? `${model}: the compiled SQL has a syntax error at line ${cause.line}, position ${cause.position}.`
        : `${model}: the compiled SQL has a syntax error.`,
  ],
  [
    "VALUE_CONVERSION_FAILED",
    ({ cause, model }) =>
      `${model}: a source value could not be converted to ${cause.targetType ?? "the target type"}. Check the column's data type at the source.`,
  ],
  [
    "STATEMENT_TIMEOUT",
    ({ model }) => `${model}: the query reached the statement or warehouse timeout.`,
  ],
  [
    "WAREHOUSE_UNREACHABLE",
    ({ cause }) =>
      cause.warehouseCode
        ? `dbt could not reach Snowflake (error ${cause.warehouseCode}).`
        : "dbt could not reach Snowflake.",
  ],
  [
    "NO_CURRENT_DATABASE",
    () =>
      "The dbt session has no current database. Check the database on the instance's Snowflake binding and the dbt role's access to it.",
  ],
  [
    "WAREHOUSE_ERROR",
    ({ cause, subject }) => {
      const who = subject ?? "The dbt run";
      if (!cause.warehouseCode) return `${who} failed in the warehouse.`;
      const state = cause.sqlState ? ` (${cause.sqlState})` : "";
      // #1662: a code the hint table knows says what happened, from the code, never the message.
      const kind = warehouseErrorKind(cause.warehouseCode, cause.sqlState);
      const words = kind ? `: ${WAREHOUSE_ERROR_HINTS[kind].words}` : "";
      return `${who} failed in the warehouse with error ${cause.warehouseCode}${state}${words}.`;
    },
  ],
  ["MODEL_COMPILATION_ERROR", ({ cause, model }) => compilationSentence(cause, model)],
  ["DBT_RUNTIME_ERROR", ({ model }) => `${model} failed with a dbt runtime error.`],
  [
    "UPSTREAM_FAILED",
    ({ cause, model }) =>
      cause.upstream
        ? `${model} was skipped because ${dbtNodeName(cause.upstream)} failed.`
        : `${model} was skipped because a model it depends on failed.`,
  ],
  [
    "UNCLASSIFIED",
    ({ subject }) => `${subject ?? "The dbt run"} failed. The cause could not be classified.`,
  ],
  [
    "TEST_UNIQUE_FAILED",
    ({ column, testModel, failures }) => {
      if (failures === undefined)
        return `Values of ${column} in ${testModel} occur more than once.`;
      const verb = agree(failures, "occurs", "occur");
      return `${plural(failures, "value", "values")} of ${column} in ${testModel} ${verb} more than once.`;
    },
  ],
  [
    "TEST_NOT_NULL_FAILED",
    ({ column, testModel, failures }) => {
      if (failures === undefined) return `Rows of ${testModel} have no ${column}.`;
      const verb = agree(failures, "has", "have");
      return `${plural(failures, "row", "rows")} of ${testModel} ${verb} no ${column}.`;
    },
  ],
  [
    "TEST_RELATIONSHIPS_FAILED",
    ({ cause, column, testModel, failures }) => {
      const parentColumn = cause.parentColumn ? `.${cause.parentColumn}` : "";
      const parent = cause.parent ? `${cause.parent}${parentColumn}` : "the parent model";
      const rows = failures === undefined ? "Rows" : `${plural(failures, "row", "rows")}`;
      const verb = failures === 1 ? "has" : "have";
      return `${rows} of ${testModel}.${column} ${verb} no match in ${parent}.`;
    },
  ],
  [
    "TEST_ACCEPTED_VALUES_FAILED",
    ({ column, testModel, failures }) => {
      if (failures === undefined) {
        return `Values of ${column} in ${testModel} are outside the accepted list.`;
      }
      const verb = agree(failures, "is", "are");
      return `${plural(failures, "value", "values")} of ${column} in ${testModel} ${verb} outside the accepted list.`;
    },
  ],
  [
    "TEST_SINGULAR_FAILED",
    ({ cause, subject, failures }) => {
      const name = cause.testName ?? subject ?? "A data test";
      return `Data test ${name} returned ${foundRows(failures)}.`;
    },
  ],
  [
    "TEST_GENERIC_FAILED",
    ({ cause, subject, testModel, failures }) => {
      const name = cause.testName ?? subject ?? "A test";
      const found = foundRows(failures);
      return cause.model
        ? `Test ${name} on ${testModel} found ${found}.`
        : `Test ${name} found ${found}.`;
    },
  ],
  [
    "REF_NOT_FOUND",
    ({ cause, model }) => {
      const target = cause.target ?? "a model";
      return cause.disabled === true
        ? `${model} depends on ${target}, which is disabled in the dbt project. Nothing was built in this run.`
        : `${model} depends on ${target}, which the dbt project does not define. Nothing was built in this run.`;
    },
  ],
  [
    "SOURCE_NOT_FOUND",
    ({ cause, model }) =>
      `${model} reads source ${cause.target ?? "a source"}, which the dbt project does not declare. Nothing was built in this run.`,
  ],
  [
    "PROJECT_PARSE_ERROR",
    ({ cause }) =>
      cause.path
        ? `The dbt project could not be parsed (${cause.path}). Nothing was built in this run.`
        : "The dbt project could not be parsed. Nothing was built in this run.",
  ],
  [
    "SELECTOR_NOT_VALID",
    () => "A dbt job has a selector dbt cannot use. Nothing was built in this run.",
  ],
  ["MODEL_TARGETS_LANDED_TABLE", ({ cause, model }) => landedTableSentence(cause, model)],
  ["DUPLICATE_RESOURCE", ({ cause }) => duplicateSentence(cause)],
]);

/**
 * #1674: a failed checkout by its reason. The branch is named only when the caller passes it: the
 * step's `metrics.dbtRef`, which the server keeps for a reader holding `instance.binding.read`
 * (#1664). A repository git could not read never speaks of a branch.
 */
function cloneSentence(reason: DbtCloneReason | null, dbtRef: string | null): string | null {
  switch (reason) {
    case "ref_not_found":
      return dbtRef
        ? `The dbt branch ${dbtRef} was not found in the repository.`
        : "The dbt branch was not found in the repository.";
    case "repo_unreadable":
      return "The dbt repository was not found or the token cannot read it.";
    case "auth":
      return "The dbt repository refused the access token.";
    default:
      return null;
  }
}

/** What a sentence may name beyond the cause's own identifiers. */
export interface DbtCauseSentenceContext {
  /** #1674: the ref the run cloned (`metrics.dbtRef`), only for a reader allowed to read it. */
  dbtRef?: string | null;
}

function sentenceFor(cause: DbtCause, context: DbtCauseSentenceContext = {}): string {
  const clone = cloneSentence(dbtCloneReasonOf(cause), context.dbtRef?.trim() || null);
  if (clone !== null) return clone;
  const subject = cause.node ? dbtNodeName(cause.node) : null;
  if (subject === null) {
    const withoutNode = sentenceWithoutNode(cause);
    if (withoutNode !== null) return withoutNode;
  }
  const sentence = SENTENCES.get(cause.code);
  if (sentence) {
    return sentence({
      cause,
      subject,
      model: subject ?? "A model",
      testModel: cause.model ? dbtNodeName(cause.model) : "the model",
      column: cause.column ?? "the tested column",
      failures: cause.failures,
    });
  }
  // The code names the phase; `exitClass` only speaks for a code that names none (review
  // s7-O3 r3 nit 3: a PHASE_CLONE cause carrying exitClass "auth" still reads "clone").
  const phase = PHASE_WORDS[cause.code] ?? cause.exitClass ?? "unknown";
  return `The dbt run failed in the ${phase} phase.`;
}

/**
 * #1391, #1430: when several tests failed, one test's kind and sentence would read as if it were
 * the only one, so every screen that has the run's test count says the total. Null when the rule
 * does not apply (not a test cause, no count, or one test). The one rule for the run page and
 * the alert.
 */
export function dbtTestsFailedSentence(
  code: DbtCauseCode,
  testsFailed: number | null | undefined,
): string | null {
  if (!code.startsWith("TEST_") || typeof testsFailed !== "number" || testsFailed <= 1) return null;
  return `${testsFailed} dbt tests did not pass.`;
}

/**
 * #1662: the hint kind of a `WAREHOUSE_ERROR` cause, from its Snowflake code and SQLSTATE only
 * (`shared/warehouse/warehouseErrorKinds.ts`), or null for any other cause or an unknown code.
 */
export function dbtWarehouseKind(
  cause: Pick<DbtCause, "code" | "warehouseCode" | "sqlState">,
): WarehouseErrorKind | null {
  if (cause.code !== "WAREHOUSE_ERROR") return null;
  return warehouseErrorKind(cause.warehouseCode, cause.sqlState);
}

/** The sentence for a cause, plain text, from identifiers only. */
export function describeDbtCause(cause: DbtCause, context: DbtCauseSentenceContext = {}): string {
  const sentence = sentenceFor(cause, context);
  return cause.severity === "warn" ? `Warning: ${sentence}` : sentence;
}

/**
 * #965: what a person can do about a cause, when that is more than the sentence already says.
 * `detail` carries the cause's closed `cloneReason` (#1674, `PHASE_CLONE`) and `warehouseKind`
 * (#1662, `dbtWarehouseKind`, `WAREHOUSE_ERROR`).
 * A code with no distinct fix returns null, and the run's "What to do" row is left out rather
 * than repeating the message.
 */
export function dbtFixFor(
  code: DbtCauseCode,
  detail: { cloneReason?: string | null; warehouseKind?: WarehouseErrorKind | null } = {},
): string | null {
  const { cloneReason, warehouseKind } = detail;
  if (code === "PHASE_CLONE") return CLONE_FIXES.get(cloneReason ?? "") ?? null;
  switch (code) {
    case "WAREHOUSE_ERROR":
      // #1662: a warehouse code the hint table knows has its own fix, else none.
      return warehouseKind ? WAREHOUSE_ERROR_HINTS[warehouseKind].fix : null;
    case "RELATION_NOT_FOUND":
      return "Import the source connection first, then run dbt again. Check that the dbt role can see the table.";
    case "PHASE_COMPILE":
      // #965, the compile case: the usual reason dbt stops before it builds is a dbt job whose
      // selector names a method or a model it cannot resolve. dbt's own compile message names no
      // dbt job (the runner's refused selector has its own cause below), so the words do not
      // point at the message. They name who can act, so a reader who cannot edit a dbt job is
      // not told to.
      return "Check the selectors of the dbt jobs in this run. An instance admin can edit them under Transformations. Use tag:, path: or a model name. Then run dbt again. If the selectors are right, check the models in your dbt project.";
    case "REF_NOT_FOUND":
      // #1367: the message names the model and the ref it cannot find.
      return "Add, enable or rename the model the ref names in your dbt project. A contributor can then run dbt again.";
    case "SOURCE_NOT_FOUND":
      return "Declare the source in your dbt project's sources file, or correct the name the model uses. A contributor can then run dbt again.";
    case "MODEL_COMPILATION_ERROR":
      // #1435: the code also covers dbt's ref cycle (a cause with `nodes`), so the fix names both.
      return "Fix the model's SQL or Jinja in your dbt project repository, or remove the circular ref between the models the cause names. A contributor can then run dbt again.";
    case "TEST_UNIQUE_FAILED":
    case "TEST_NOT_NULL_FAILED":
    case "TEST_RELATIONSHIPS_FAILED":
    case "TEST_ACCEPTED_VALUES_FAILED":
    case "TEST_SINGULAR_FAILED":
    case "TEST_GENERIC_FAILED":
    case "PHASE_TEST":
      // #1387: the models built, so the data or the test is what to change.
      return "Fix the data or the test. A contributor can then run dbt again.";
    case "MODEL_TARGETS_LANDED_TABLE":
      // #1442: nothing was built, so the loaded table is as the connection left it.
      return "Give the model another name, or set a schema or an alias for it in your dbt project, so it no longer builds where the connection loads its tables. Nothing was built in this run. A contributor can then run dbt again.";
    case "SELECTOR_NOT_VALID":
      return "Fix the selector of the dbt job the message names. An instance admin can edit it under Transformations. Use tag:, path: or a model name. Then run dbt again.";
    default:
      return null;
  }
}

/**
 * #1674: the fix of a failed checkout by its reason; an unknown reason has none. Each names who can
 * act, as the other dbt fixes do (review of PR #1689): the run page and the run email reach every
 * role, and Settings, Destination is Instance admin and above (`instance.binding.read`).
 */
const CLONE_FIXES: ReadonlyMap<string, string> = new Map([
  [
    "ref_not_found",
    "An instance admin can check the dbt branch in Settings, Destination, dbt repository.",
  ],
  [
    "repo_unreadable",
    "An instance admin can check the repository address and the access token in Settings, Destination, dbt repository.",
  ],
  [
    "auth",
    "An instance admin can replace the access token in Settings, Destination, dbt repository.",
  ],
]);

/** #1674: the badge of a failed checkout by its reason; an unknown reason keeps "Clone failed". */
const CLONE_LABELS: ReadonlyMap<string, string> = new Map([
  ["ref_not_found", "Branch not found"],
  // Review of PR #1689: the reason covers a repository the token cannot read as well.
  ["repo_unreadable", "Repository not readable"],
  ["auth", "Token refused"],
]);

const LABELS: Record<DbtCauseCode, string> = {
  COLUMN_NOT_FOUND: "Column not found",
  RELATION_NOT_FOUND: "Relation not found",
  SQL_SYNTAX_ERROR: "SQL syntax error",
  VALUE_CONVERSION_FAILED: "Value conversion failed",
  STATEMENT_TIMEOUT: "Statement timeout",
  WAREHOUSE_UNREACHABLE: "Warehouse unreachable",
  NO_CURRENT_DATABASE: "No current database",
  WAREHOUSE_ERROR: "Warehouse error",
  MODEL_COMPILATION_ERROR: "Compilation error",
  DBT_RUNTIME_ERROR: "dbt runtime error",
  UPSTREAM_FAILED: "Upstream failed",
  UNCLASSIFIED: "Not classified",
  TEST_UNIQUE_FAILED: "Unique test failed",
  TEST_NOT_NULL_FAILED: "Not-null test failed",
  TEST_RELATIONSHIPS_FAILED: "Relationships test failed",
  TEST_ACCEPTED_VALUES_FAILED: "Accepted values test failed",
  TEST_SINGULAR_FAILED: "Data test failed",
  TEST_GENERIC_FAILED: "Test failed",
  REF_NOT_FOUND: "Missing ref",
  SOURCE_NOT_FOUND: "Missing source",
  PROJECT_PARSE_ERROR: "Project parse error",
  DUPLICATE_RESOURCE: "Duplicate resource",
  SELECTOR_NOT_VALID: "Selector not valid",
  MODEL_TARGETS_LANDED_TABLE: "Model would overwrite a loaded table",
  PHASE_CONFIG: "Configuration failed",
  PHASE_AUTH: "Authentication failed",
  PHASE_CLONE: "Clone failed",
  PHASE_DEPS: "dbt deps failed",
  PHASE_COMPILE: "Compile failed",
  PHASE_RUN: "Run failed",
  PHASE_TEST: "Tests failed",
  PHASE_INFRA: "Infrastructure error",
  PHASE_TIMEOUT: "Timed out",
  PHASE_INTERNAL: "Internal error",
  PHASE_STALE: "Source not fresh",
};

/** The short badge label for a code. */
export function labelDbtCause(code: DbtCauseCode, cloneReason?: string | null): string {
  if (code === "PHASE_CLONE") return CLONE_LABELS.get(cloneReason ?? "") ?? LABELS[code];
  return LABELS[code];
}

/** True for the `PHASE_*` fallbacks, which say no more than the exit-class clause beside them. */
export function isDbtPhaseCause(cause: Pick<DbtCause, "code">): boolean {
  return cause.code.startsWith("PHASE_");
}
